import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const Panel = styled.aside`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  border: 1px solid ${({ theme }) => `${theme.green}1f`};
  background:
    linear-gradient(180deg, ${({ theme }) => `${theme.green}0d`} 0%, transparent 100%),
    ${({ theme }) => theme.cardBg};
  border-radius: 18px;
  padding: 20px;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow:
    inset 0 1px 0 ${({ theme }) => `${theme.white}08`},
    0 18px 40px rgba(0,0,0,0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Title = styled.h3`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.white};
  font-family: "SpaceGrotesk";
  font-size: 22px;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
`;

const CreateButton = styled.button`
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.green};
  background: ${({ theme }) => theme.green};
  color: ${({ theme }) => theme.black};
  border-radius: 10px;
  min-width: 120px;
  padding: 10px 18px;
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  white-space: nowrap;
  box-shadow: 0 0 24px ${({ theme }) => `${theme.green}2e`};
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 28px ${({ theme }) => `${theme.green}40`};
    filter: brightness(1.04);
  }
`;

const SearchBar = styled.label`
  box-sizing: border-box;
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.blueish2};
  pointer-events: none;
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: ${({ theme }) => `${theme.lines}cc`};
  border: 1px solid ${({ theme }) => `${theme.green}18`};
  border-radius: 12px;
  padding: 12px 14px 12px 40px;
  color: ${({ theme }) => theme.white};
  outline: none;
  font-family: "SatoshiRegular";
  font-size: 13px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &::placeholder {
    color: ${({ theme }) => theme.blueish2};
  }

  &:focus {
    border-color: ${({ theme }) => `${theme.green}70`};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.green}16`};
    background: ${({ theme }) => theme.lines};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CountRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
`;

const CountBadge = styled.span`
  min-width: 32px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => `${theme.green}33`};
  background: ${({ theme }) => `${theme.green}12`};
  color: ${({ theme }) => theme.green};
  font-family: "SatoshiBold";
  font-size: 12px;
  line-height: 1.1;
  text-align: center;
`;

const CountLabel = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiBold";
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
  min-height: 0;
  width: 100%;
`;

const TeamCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  border: 1px solid ${({ theme, $active }) => ($active ? `${theme.green}5c` : `${theme.green}18`)};
  background:
    linear-gradient(180deg, ${({ theme, $active }) => ($active ? `${theme.green}12` : "transparent")} 0%, transparent 100%),
    ${({ theme, $active }) => ($active ? theme.lines : `${theme.lines}d9`)};
  border-radius: 14px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: ${({ theme }) => `${theme.green}4f`};
    box-shadow:
      0 0 0 1px ${({ theme }) => `${theme.green}12`},
      0 12px 28px rgba(0,0,0,0.1);
    transform: translateY(-1px);
  }
`;

const AccentBar = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${({ theme }) => theme.green} 0%, transparent 100%);
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.18s ease;

  ${TeamCard}:hover & {
    opacity: 1;
  }
`;

const TeamCardBody = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const FormationBadge = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $active }) => ($active ? theme.green : theme.blueish)};
  background: ${({ theme, $active }) => ($active ? `${theme.green}14` : theme.cardBgAlt)};
  border: 1px solid ${({ theme, $active }) => ($active ? `${theme.green}40` : `${theme.green}1a`)};
`;

const TeamInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamName = styled.p`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 14px;
  line-height: 1.25;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TeamMetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => `${theme.green}1f`};
  background: ${({ theme }) => theme.cardBgAlt};
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiBold";
  font-size: 10px;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  padding: 5px 8px;
`;

const MetaDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ theme, $active }) => ($active ? theme.green : theme.blueish2)};
  box-shadow: ${({ theme, $active }) => ($active ? `0 0 10px ${theme.green}` : "none")};
  flex-shrink: 0;
`;

const PlayersCount = styled.span`
  color: ${({ theme, $active }) => ($active ? theme.blueish : theme.blueish2)};
  font-family: "SatoshiRegular";
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
`;

const TeamActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
`;

const TeamActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme, $danger }) => ($danger ? `${theme.red}40` : `${theme.green}28`)};
    background: ${({ theme, $danger }) => ($danger ? `${theme.red}10` : `${theme.green}10`)};
    color: ${({ theme, $danger }) => ($danger ? theme.red : theme.white)};
  }
`;

const EmptyState = styled.div`
  flex: 1;
  border: 1px dashed ${({ theme }) => `${theme.green}20`};
  background: ${({ theme }) => `${theme.lines}b3`};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 28px 24px;
`;

const EmptyIcon = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.blueish2};
  background: ${({ theme }) => theme.cardBgAlt};
  border: 1px solid ${({ theme }) => `${theme.green}1f`};
`;

const EmptyTitle = styled.p`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 15px;
  line-height: 1.3;
`;

const EmptyBody = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 13px;
  line-height: 1.5;
  max-width: 240px;
`;

const EmptyActionButton = styled.button`
  margin-top: 4px;
  border: 1px solid ${({ theme }) => `${theme.green}2e`};
  background: ${({ theme }) => `${theme.green}14`};
  color: ${({ theme }) => theme.green};
  border-radius: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &:hover {
    background: ${({ theme }) => `${theme.green}1c`};
  }
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="6.1" cy="6.1" r="4.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.4 9.4L12.2 12.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const FormationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="3.25" y="2.75" width="15.5" height="16.5" rx="3.25" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.5 8H18.5M3.5 14H18.5" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
    <circle cx="11" cy="6" r="1.35" fill="currentColor" />
    <circle cx="7" cy="11" r="1.35" fill="currentColor" opacity="0.85" />
    <circle cx="15" cy="11" r="1.35" fill="currentColor" opacity="0.85" />
    <circle cx="11" cy="16" r="1.35" fill="currentColor" opacity="0.7" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M3.2 10.9L3.7 8.7L9.4 3.1C9.9 2.5 10.8 2.5 11.4 3.1L11.9 3.6C12.5 4.2 12.5 5.1 11.9 5.7L6.3 11.3L4.1 11.8L3.2 10.9Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M8.7 3.8L11.2 6.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M2.8 4.2H12.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M5.7 2.9H9.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path
      d="M4.3 4.2L4.8 11.1C4.9 11.8 5.4 12.3 6 12.3H9C9.6 12.3 10.1 11.8 10.2 11.1L10.7 4.2"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6.5 6.3V10M8.5 6.3V10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const EmptyStateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2.25" y="2.25" width="13.5" height="13.5" rx="3.25" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5.5 9H12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 5.5V12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ShadowTeamsList = ({
  teamCards = [],
  onSelect,
  onCreate,
  onEdit,
  onDelete,
  tutorialPanelRef = null,
  tutorialCreateButtonRef = null,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredTeamCards = useMemo(() => {
    if (!normalizedQuery) {
      return teamCards;
    }

    return teamCards.filter((team) =>
      String(team.name || "")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [normalizedQuery, teamCards]);

  const hasTeams = teamCards.length > 0;
  const showFilteredEmptyState = hasTeams && !filteredTeamCards.length;

  return (
    <Panel ref={tutorialPanelRef}>
      <Header>
        <Title>{t("shadowTeam.listTitle")}</Title>
        <CreateButton ref={tutorialCreateButtonRef} type="button" onClick={onCreate}>
          {t("shadowTeam.newTeamButton")}
        </CreateButton>
      </Header>

      <SearchBar>
        <SearchIconWrap>
          <SearchIcon />
        </SearchIconWrap>
        <SearchInput
          type="text"
          value={searchQuery}
          disabled={!hasTeams}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t("shadowTeam.searchTeamsPlaceholder")}
          aria-label={t("shadowTeam.searchTeamsPlaceholder")}
          data-testid="shadow-team-search-input"
        />
      </SearchBar>

      <CountRow>
        <CountBadge data-testid="shadow-team-total-count">{teamCards.length}</CountBadge>
        <CountLabel>{t("shadowTeam.teamsSaved")}</CountLabel>
      </CountRow>

      {!hasTeams ? (
        <EmptyState data-testid="shadow-team-empty-state">
          <EmptyIcon>
            <EmptyStateIcon />
          </EmptyIcon>
          <EmptyTitle>{t("shadowTeam.listEmptyTitle")}</EmptyTitle>
          <EmptyBody>{t("shadowTeam.listEmptyBody")}</EmptyBody>
        </EmptyState>
      ) : showFilteredEmptyState ? (
        <EmptyState data-testid="shadow-team-search-empty-state">
          <EmptyIcon>
            <SearchIcon />
          </EmptyIcon>
          <EmptyTitle>{t("shadowTeam.searchEmptyTitle")}</EmptyTitle>
          <EmptyBody>{t("shadowTeam.searchEmptyBody")}</EmptyBody>
          <EmptyActionButton type="button" onClick={() => setSearchQuery("")}>
            {t("shadowTeam.clearSearch")}
          </EmptyActionButton>
        </EmptyState>
      ) : (
        <TeamList>
          {filteredTeamCards.map((team) => {
            const displayName = team.name || `#${team.id}`;

            return (
              <TeamCard
                key={team.id}
                $active={team.isActive}
                role="button"
                tabIndex={0}
                aria-label={displayName}
                aria-pressed={team.isActive}
                data-testid={`shadow-team-card-${team.id}`}
                onClick={() => onSelect?.(team.id)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) {
                    return;
                  }

                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect?.(team.id);
                  }
                }}
              >
                <AccentBar $active={team.isActive} />

                <TeamCardBody>
                  <FormationBadge $active={team.isActive}>
                    <FormationIcon />
                  </FormationBadge>

                  <TeamInfo>
                    <TeamName title={displayName}>{displayName}</TeamName>

                    <TeamMetaRow>
                      <MetaPill>
                        <MetaDot $active={team.isActive} />
                        {team.formationLabel || t("shadowTeam.formation")}
                      </MetaPill>

                      {team.completion ? (
                        <PlayersCount $active={team.isActive}>
                          {t("shadowTeam.teamCompletion", team.completion)}
                        </PlayersCount>
                      ) : null}
                    </TeamMetaRow>
                  </TeamInfo>

                  <TeamActions>
                    <TeamActionButton
                      type="button"
                      aria-label={t("shadowTeam.editTeam")}
                      title={t("shadowTeam.editTeam")}
                      data-testid={`shadow-team-edit-${team.id}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit?.(team);
                      }}
                    >
                      <EditIcon />
                    </TeamActionButton>

                    <TeamActionButton
                      type="button"
                      $danger
                      aria-label={t("shadowTeam.delete")}
                      title={t("shadowTeam.delete")}
                      data-testid={`shadow-team-delete-${team.id}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete?.(team);
                      }}
                    >
                      <DeleteIcon />
                    </TeamActionButton>
                  </TeamActions>
                </TeamCardBody>
              </TeamCard>
            );
          })}
        </TeamList>
      )}
    </Panel>
  );
};

export default ShadowTeamsList;
